// The race's sound: the original kissaderby.fi/modern clips. Create once; unlock on the Start
// click (browsers allow sound only after a user gesture); feed race events every frame.
import type { RaceEvent, RaceSnapshot } from '../race/types';
import { clipUrl, MUSIC_CLIPS } from './clips';
import { AudioDirector } from './director';
import { ClipPlayer, HtmlAudioOut, type ClipOut } from './player';

export interface AudioSystem {
  /** Call from the Start click (a user gesture). */
  unlock(): void;
  setMuted(muted: boolean): void;
  /** Master volume 0..1. */
  setVolume(volume: number): void;
  update(snapshot: RaceSnapshot | null, events: RaceEvent[], dt: number): void;
  /** For the test hook: the long clip playing ("music") and the most recent clips played. */
  state(): { unlocked: boolean; music: string; cues: { cue: string; at: number; raceT: number }[]; playing: string[]; volume: number; soundOn: boolean };
}

export function createAudio(rand: () => number = Math.random, out?: ClipOut, clock: () => number = () => performance.now()): AudioSystem {
  const player = new ClipPlayer(out ?? new HtmlAudioOut(clipUrl), rand);
  const director = new AudioDirector(player, rand);
  let unlocked = false;
  let muted = true;
  const apply = () => { player.tick(clock()); player.setOn(unlocked && !muted); };

  return {
    unlock() { unlocked = true; apply(); },
    // Unmuting is always a click, so it may unlock sound too (e.g. with ?autostart, which skips Start).
    setMuted(m) { muted = m; if (!m) unlocked = true; apply(); },
    setVolume(v) { player.setVolume(v); },
    state() {
      const playing = player.current();
      const music = playing.map((f) => MUSIC_CLIPS[f]).find(Boolean) ?? 'none';
      return { unlocked, music, cues: player.log.map((l) => ({ cue: l.file, at: l.at, raceT: l.raceT })), playing, volume: player.volume, soundOn: player.soundOn };
    },
    update(snapshot, events) {
      try {
        const now = clock();
        if (snapshot) player.raceT = snapshot.t;
        player.tick(now);
        director.update(snapshot, events, now);
      } catch (err) {
        console.warn('[audio]', err); // sound must never break the race
      }
    },
  };
}
